import { faWindows } from '@fortawesome/free-brands-svg-icons';
import {
    faMap,
    faTag,
    faDownload,
    faMagnifyingGlass,
    faBell,
    faFolderOpen,
} from '@fortawesome/free-solid-svg-icons';
import { faCameraCctv } from '@fortawesome/pro-solid-svg-icons';
import { createStackNavigator } from '@react-navigation/stack';
import AlertGroups from '../pages/cameras/AlertGroups/AlertGroups';
import CameraPlayer from '../pages/cameras/CameraPlayer/CameraPlayer';
import CameraDetails from '../pages/cameras/Cameras/CameraDetails';
import CamerasList from '../pages/cameras/Cameras/CamerasList';
import CamerasMap from '../pages/cameras/CamerasMap/CamerasMap';
import CameraTags from '../pages/cameras/CameraTags/CameraTags';
import Downloads from '../pages/cameras/Downloads/Downloads';
import DownloadTags from '../pages/cameras/DownloadTags/DownloadTags';
import Mosaic from '../pages/cameras/Mosaic/Mosaic';
import MosaicDetail from '../pages/cameras/Mosaic/MosaicDetail';
import VideoAnalysis from '../pages/cameras/VideoAnalysis/VideoAnalysis';
import VideoAnalysisDetails from '../pages/cameras/VideoAnalysisDetails';
import CamerasReports from '../pages/cameras/Reports/Reports';
import { Module } from '../Routes';
import { CamerasSchemas } from '../schemas/cameras';
import { CamerasParamList } from '../typings/Params';
import CameraMapPlayer from '../pages/cameras/CameraPlayer/CameraMapPlayer';
import UpsertAlertGroupModal from '../pages/cameras/AlertGroups/UpsertAlertGroupModal';


export const camerasModule: Module<'Cameras'> = {
    stackNavigator: createStackNavigator<CamerasParamList>(),
    path: '/cameras',
    label: 'cameras',
    canAccess: (user, currentApp) => Boolean(user && !currentApp.isAgentApp),
    icon: require('../../assets/icon_cameras.png'),
    logo: require('../../assets/logo_cameras.png'),
    navigate: (navigation) => navigation.navigate('Cameras'),
    schemas: CamerasSchemas,
    screens: {
        CamerasList: {
            path: 'cameras',
            selectedItem: 'cameras',
            initialParams: () => ({
                page: 1,
                online: true,
                limit: 25,
            }),
            label: 'cameras',
            component: CamerasList,
            accessInformation: {
                icon: faCameraCctv,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CamerasList' }),
            },
            canAccess: () => true,
        },
        CameraPlayer: {
            path: 'watch/:id',
            selectedItem: 'cameras',
            component: CameraPlayer,
            label: 'camera',
            canAccess: () => true,
        },
        CameraDetails: {
            path: 'cameras/details/:id',
            selectedItem: 'cameras',
            label: 'camera',
            component: CameraDetails,
            canAccess: () => true,
        },
        Mosaic: {
            path: 'mosaic',
            selectedItem: 'mosaic',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            label: 'mosaics',
            component: Mosaic,
            accessInformation: {
                icon: faWindows,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'Mosaic' }),
            },
            canAccess: () => true,
        },
        MosaicDetail: {
            path: 'mosaic/:id',
            selectedItem: 'mosaic',
            label: 'mosaic',
            component: MosaicDetail,
            canAccess: () => true,
        },
        CamerasMap: {
            path: 'map',
            selectedItem: 'map',
            label: 'map',
            component: CamerasMap,
            accessInformation: {
                icon: faMap,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CamerasMap' }),
            },
            canAccess: () => true,
        },
        CameraMapPlayer: {
            path: 'map/:id',
            selectedItem: 'map',
            component: CameraMapPlayer,
            label: 'camera',
            canAccess: () => true,
        },
        CameraTags: {
            path: 'camera-tags',
            label: 'tags',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            selectedItem: 'camera-tags',
            component: CameraTags,
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_manage_tags),
            accessInformation: {
                icon: faTag,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CameraTags' }),
            }
        },
        DownloadTags: {
            path: 'download-tags',
            selectedItem: 'download-tags',
            label: 'downloadsTags',
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            component: DownloadTags,
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_manage_download_tags),
            accessInformation: {
                icon: faTag,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'DownloadTags' }),
            }
        },
        Downloads: {
            path: 'downloads',
            selectedItem: 'downloads',
            label: 'downloads',
            component: Downloads,
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_download_video || user?.permissions.camera_watch_download),
            accessInformation: {
                icon: faDownload,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'Downloads' }),
            }
        },
        VideoAnalysis: {
            path: 'video-analysis',
            selectedItem: 'video-analysis',
            label: 'zeusAI',
            component: VideoAnalysis,
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_video_analysis || user?.permissions.camera_watch_video_analysis),
            accessInformation: {
                icon: faMagnifyingGlass,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'VideoAnalysis' }),
            }
        },
        VideoAnalysisDetails: {
            path: 'video-analysis/details/:id',
            selectedItem: 'video-analysis',
            label: 'zeusAI',
            component: VideoAnalysisDetails,
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.camera_video_analysis || user?.permissions.camera_watch_video_analysis),
        },
        AlertGroups: {
            path: 'alert-groups',
            selectedItem: 'alert-groups',
            label: 'alertGroups',
            component: AlertGroups,
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.manage_camera_alert_groups),
            accessInformation: {
                icon: faBell,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'AlertGroups' }),
            }
        },
        UpsertAlertGroupModal: {
            path: 'alert-groups/form/:id?',
            selectedItem: 'alert-groups',
            component: UpsertAlertGroupModal,
            label: 'alertGroups',
            canAccess: (user) => Boolean(user?.isAdmin || user?.permissions.manage_camera_alert_groups),
            presentation: 'transparentModal',
            parentScreen: 'AlertGroups',
        },
        CamerasReports: {
            path: 'cameras-reports',
            selectedItem: 'cameras-reports',
            label: 'reports',
            component: CamerasReports,
            initialParams: () => ({
                page: 1,
                limit: 25,
            }),
            canAccess: (user) => Boolean(user?.permissions.camera_offline_historic_report),
            accessInformation: {
                icon: faFolderOpen,
                navigate: (navigation) => navigation.navigate('Cameras', { screen: 'CamerasReports' }),
            }
        }
    },
};
